import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { EventFilter, NativePlatform } from '@fullstory/proto/fs/api/segment';
import {
  SignalStudioComponentProps,
  SignalStudioContext,
  usePreviewSearch,
} from '@fullstory/signal-studio-context';
import { useIntl } from 'react-intl';
import { SidebarElementPropertyRule } from '../components/SidebarElementPropertyRule';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import {
  ElementPropertyRuleRecord,
  ElementPropertyRule,
} from '@fullstory/proto/fs/services/search-settings';
import {
  ElementPropertyRuleErrors,
  ElementPropertyRuleModalSource,
  ElementPropertyRuleModalVariant,
} from '../components';
import {
  makeAvailableMultiRawValues,
  makeAvailableSingleRawValues,
  makePlatformSelectorDeps,
} from '../utils';
import { useValidateElementPropertyRule } from '../components/ElementPropertyRuleModal/ElementPropertyRuleDetailsModal';
import { isEqual } from 'lodash';
import { FsEvent } from '@fullstory/fs-script-api';
import { containsNonDescendantCombinator } from '@fullstory/selectors';
import { AvailableRawValues } from '../types';
import { assertExhaustive } from '@fullstory/typeutils';
import { useElementPropertyRulesWarnings } from './useElementPropertyRulesWarnings';
import { multiValueInfo } from '@fullstory/util/vars';
import { useElementPropertyRuleSelectors } from './useElementPropertyRuleSelectors';
import { useMatchesPrivacyRuleSelector } from './useMatchesPrivacyRuleSelector';
import { useElementPropertyRuleHighlights } from './useElementPropertyRuleHighlights';
import { useElementPropertyRuleConfig } from './useElementPropertyRuleConfig';

const EMPTY_ERRORS: ElementPropertyRuleErrors = {};

enum IndexType {
  BASE,
  PARENT,
}

interface Index {
  type: IndexType;
  index: number;
}

export interface useElementPropertyRuleSignalStudioProps {
  elementPropertyRuleRecord: ElementPropertyRuleRecord;
  onCloseSignal: (saved?: boolean) => void;
  variant: ElementPropertyRuleModalVariant;
  onSave: (elementPropertyRule: ElementPropertyRule) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  source: ElementPropertyRuleModalSource;
}

export const useElementPropertyRuleSignalStudio = (
  props: useElementPropertyRuleSignalStudioProps,
): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: SignalStudioComponentProps;
} => {
  const intl = useIntl();
  const flags = useFlags();
  const canModifyProperties = flags.hasBit(Flag.PermModifyCustomVars);
  const SignalStudio = useContext(SignalStudioContext);

  const { elementPropertyRule, setElementPropertyRule } = useElementPropertyRuleConfig(
    props.elementPropertyRuleRecord.elementPropertyRule!,
  );
  const { isMulti } = multiValueInfo(elementPropertyRule.propertyType);

  const [trimTrigger, setTrimTrigger] = useState<boolean>(false);

  const [activeIndex, _setActiveIndex] = useState<Index>({ type: IndexType.BASE, index: 0 });
  const [previewState, setPreviewState] = useState(0);
  const incrementPreviewState = useCallback(() => setPreviewState(current => current + 1), []);

  const eventFiltersPreview = useMemo(
    (): (EventFilter | null)[] => [
      // prefer click
      { click: { any: {} } },
      // but fall back to any activity if no click data is available
      null,
    ],
    [],
  );

  const {
    selectors,
    childSelectors,
    hasNamedElementSelectors,
    hasChild,
    hasChildAndNonEmptyParent,
  } = useElementPropertyRuleSelectors(elementPropertyRule);

  const isPrivacyRuleSelector = useMatchesPrivacyRuleSelector(
    hasChild ? childSelectors : selectors,
  );

  const { fallbackDepsForChild, fallbackDepsForParent } = useMemo(() => {
    const deps = (selectors ?? []).map(makePlatformSelectorDeps);
    const intersectionDeps = deps
      .filter(deps => deps.length)
      .map(deps => ({ dependentSet: { intersection: { deps } } }));
    // Provide a single union of all selectors for when the parent is not the active selection.
    // Additionally, if the parent is a named element, we will also just use the union (since the element is a single item).
    const depsUnionForPreview = intersectionDeps.length
      ? [{ dependentSet: { union: { deps: intersectionDeps } } }]
      : [];
    // we will only change the fallback deps array if the new fallback deps are not equal -- to avoid thrash
    const fallbackDepsForParent = hasNamedElementSelectors ? [depsUnionForPreview] : deps;
    const fallbackDepsForChild = [depsUnionForPreview];
    return { fallbackDepsForChild, fallbackDepsForParent };
  }, [hasNamedElementSelectors, selectors]);

  // for better stability when switching between parent and child selectors, we also keep track of last active parent
  // as long as the last active parent index matches when we switch to a parent index, we can preserve the preview
  // for the initial value, if we have multiple parents for the existing element, we force a refresh on active selection to ensure the correct parent is shown
  const [lastActiveParentIndex, setLastActiveParentIndex] = useState<number | null>(
    hasChild && fallbackDepsForParent.length > 1 ? -1 : 0,
  );
  // layer of indirection for active index setting for handling preview state increment (when applicable)
  const setActiveIndex = useCallback(
    (index: Index, allowPreviewStateChange = true) => {
      _setActiveIndex(index);
      if (allowPreviewStateChange) {
        if (index.type === IndexType.PARENT) {
          // when we are switching to a parent index, we only change preview state if the parent index has changed from the last active one
          if (index.index !== lastActiveParentIndex) {
            incrementPreviewState();
          }
          setLastActiveParentIndex(index.index);
        } else if (!hasChild && !isEqual(activeIndex, index)) {
          // but if we have no parent child relationship, we always increment preview state when the index changes
          incrementPreviewState();
        }
      }
    },
    [hasChild, activeIndex, lastActiveParentIndex, incrementPreviewState],
  );

  const previewSearchesForActiveChild = usePreviewSearch(
    eventFiltersPreview,
    hasChild ? fallbackDepsForChild : fallbackDepsForParent,
    incrementPreviewState,
  );
  const previewSearchesForActiveParent = usePreviewSearch(
    eventFiltersPreview,
    fallbackDepsForParent,
    incrementPreviewState,
  );

  const [previewSearches, previewActiveIndex] =
    activeIndex.type === IndexType.BASE
      ? [previewSearchesForActiveChild, hasChild ? 0 : activeIndex.index]
      : [previewSearchesForActiveParent, activeIndex.index];
  const visualUnsupported = !!elementPropertyRule?.any || !!elementPropertyRule.anyChild;

  // when we have a leftover active state for the parent but no longer have the parent/child relationship
  // we want to trigger resetting the active index and related preview search data
  useEffect(() => {
    if (activeIndex.type === IndexType.PARENT && !hasChild) {
      setActiveIndex({ type: IndexType.BASE, index: 0 }, false);
      [...previewSearchesForActiveParent].reverse().forEach(search => search.remove());
    }
  }, [activeIndex, hasChild]);

  const onSetActiveIndex = useCallback(
    (index: number, type: IndexType = IndexType.BASE) => {
      const newActiveIndex = { type, index };
      const changed = !isEqual(newActiveIndex, activeIndex);
      if (changed) {
        setActiveIndex(newActiveIndex);
      }
    },
    [activeIndex, setActiveIndex],
  );
  const onSetActiveIndexParent = useCallback(
    (index: number) => {
      onSetActiveIndex(index, IndexType.PARENT);
    },
    [onSetActiveIndex],
  );
  const onSetActiveIndexParentOrBase = useCallback(
    (index: number) => {
      onSetActiveIndex(index, hasChild ? IndexType.PARENT : IndexType.BASE);
    },
    [onSetActiveIndex, hasChild],
  );

  const onDeleteIndex = useCallback(
    (index: number, type: IndexType = IndexType.BASE) => {
      if (
        type === activeIndex.type &&
        ((index === activeIndex.index && activeIndex.index > 0) || index < activeIndex.index)
      ) {
        onSetActiveIndex(activeIndex.index - 1, type);
      }
      switch (type) {
        case IndexType.BASE:
          previewSearchesForActiveChild[index]?.remove?.();
          break;
        case IndexType.PARENT:
          previewSearchesForActiveParent[index]?.remove?.();
          break;
        default:
          assertExhaustive(type);
      }
    },
    [
      activeIndex.index,
      activeIndex.type,
      onSetActiveIndex,
      previewSearchesForActiveChild,
      previewSearchesForActiveParent,
    ],
  );
  const onDeleteIndexParent = useCallback(
    (index: number) => {
      onDeleteIndex(index, IndexType.PARENT);
    },
    [onDeleteIndex],
  );

  const onInspectSelector = useCallback(
    (
      selector: string,
      canonicalizedSelector: string,
      nativePlatform: NativePlatform | undefined,
    ) => {
      if (activeIndex.type === IndexType.BASE && hasChild) {
        // we are updating a piece of the child selectors
        // unset the other options before setting the selectors
        const { anyChild, childNamedElementId, ...updatedElementPropertyRule } =
          elementPropertyRule;
        setElementPropertyRule({
          ...updatedElementPropertyRule,
          childSelectors: {
            selectors: [
              ...(updatedElementPropertyRule.childSelectors?.selectors?.slice(
                0,
                activeIndex.index,
              ) ?? []),
              {
                selector,
                originalSelector: selector,
                canonicalizedSelector,
                platforms: nativePlatform ? [nativePlatform] : [],
              },
              ...(updatedElementPropertyRule.childSelectors?.selectors?.slice(
                activeIndex.index + 1,
              ) ?? []),
            ],
          },
        });
      } else {
        // we are updating a piece of the target element / parent selectors
        // unset the other options before setting the selectors
        const { any, namedElementId, ...updatedElementPropertyRule } = elementPropertyRule;
        setElementPropertyRule({
          ...updatedElementPropertyRule,
          selectors: {
            selectors: [
              ...(updatedElementPropertyRule.selectors?.selectors?.slice(0, activeIndex.index) ??
                []),
              {
                selector,
                originalSelector: selector,
                canonicalizedSelector,
                platforms: nativePlatform ? [nativePlatform] : [],
              },
              ...(updatedElementPropertyRule.selectors?.selectors?.slice(activeIndex.index + 1) ??
                []),
            ],
          },
        });
      }
      // And finally, when applicable, auto trim after inspection.
      const shouldAutoTrim = !containsNonDescendantCombinator(selector);
      setTrimTrigger(shouldAutoTrim);
    },
    [activeIndex.type, activeIndex.index, hasChild, elementPropertyRule, setElementPropertyRule],
  );

  const {
    highlightedObjects,
    valueSourceObjects,
    previewElements,
    visibleElements,
    attachedElements,
    highlights,
    highlightConstraints,
    onHighlightedObjects,
  } = useElementPropertyRuleHighlights({
    selectors,
    childSelectors,
    hasChild,
    hasChildAndNonEmptyParent,
    isMulti,
  });

  const availableValues = useMemo((): AvailableRawValues | undefined => {
    if (visualUnsupported) {
      return undefined;
    }
    return isMulti
      ? makeAvailableMultiRawValues(valueSourceObjects)
      : makeAvailableSingleRawValues(valueSourceObjects);
  }, [visualUnsupported, valueSourceObjects, isMulti]);

  const validate = useValidateElementPropertyRule();
  const { errors, valid } = useMemo(() => {
    const errors = validate(elementPropertyRule);
    return {
      valid: isEqual(errors, {}),
      errors,
    };
  }, [elementPropertyRule, validate]);
  const [showErrors, setShowErrors] = useState(false);

  const warnings = useElementPropertyRulesWarnings({
    elementPropertyRule,
    availableValues,
    highlightedObjects,
    isPrivacyRuleSelector,
  });

  const ConfigSidebar = (
    <SidebarElementPropertyRule
      elementPropertyRule={elementPropertyRule}
      archived={props.elementPropertyRuleRecord?.metadata?.archived || false}
      onChange={setElementPropertyRule}
      availableValues={availableValues}
      variant={props.variant}
      errors={showErrors ? errors : EMPTY_ERRORS}
      visibleElements={visibleElements}
      attachedElements={attachedElements}
      activeIndex={{
        index: activeIndex.type === IndexType.BASE ? activeIndex.index : -1,
        setIndex: onSetActiveIndex,
        deleteIndex: onDeleteIndex,
      }}
      parentActiveIndex={{
        index: activeIndex.type === IndexType.PARENT ? activeIndex.index : -1,
        setIndex: onSetActiveIndexParent,
        deleteIndex: onDeleteIndexParent,
      }}
      trimTrigger={trimTrigger}
      setTrimTrigger={setTrimTrigger}
      warnings={warnings}
    />
  );

  const archived = props.elementPropertyRuleRecord?.metadata?.archived || false;
  const metadata = useMemo(() => {
    if (
      !props.elementPropertyRuleRecord?.metadata ||
      props.variant === ElementPropertyRuleModalVariant.new
    ) {
      return undefined;
    }
    return {
      createdBy: props.elementPropertyRuleRecord.metadata.createdBy,
      updatedBy: props.elementPropertyRuleRecord.metadata.lastUpdatedBy,
      createdAt: props.elementPropertyRuleRecord.metadata.created,
      updatedAt: props.elementPropertyRuleRecord.metadata.lastUpdated,
    };
  }, [props.elementPropertyRuleRecord?.metadata, props.variant]);

  return {
    SignalStudio,
    signalStudioProps: {
      header: elementPropertyRule?.name ?? '',
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' }),
      configSidebar: ConfigSidebar,
      preview: {
        elements: previewElements,
        state: previewState,
        index: previewActiveIndex,
        onIndexChange: onSetActiveIndexParentOrBase,
        mode: 'element-inspector',
        previews: previewSearches.map(search => ({
          search,
          visualUnsupportedMessage: visualUnsupported
            ? intl.formatMessage({
                defaultMessage: "Previews are not supported for 'any' rules.",
                id: 'Lr7jUa',
              })
            : undefined,
        })),
        highlights,
        highlightConstraints,
        onHighlightedObjects,
        onInspectSelector,
      },
      onConfirm: () => {
        setShowErrors(!valid);
        if (valid) {
          FsEvent.recordCustomEventV2('Property Extraction Rule Saved', {
            is_new: props.variant === ElementPropertyRuleModalVariant.new,
            source: ElementPropertyRuleModalSource[props.source],
            id: elementPropertyRule?.id ?? '',
          });
          props.onSave(elementPropertyRule);
          props.onCloseSignal(true);
        }
      },
      onCloseSignal: () => {
        props.onCloseSignal(false);
      },
      onArchive: () => {
        if (props.variant === ElementPropertyRuleModalVariant.new || !elementPropertyRule.id) {
          return;
        }
        const customEventName = 'Property Extraction Rule Archived';
        FsEvent.recordCustomEventV2(customEventName, {
          id: elementPropertyRule.id,
          archived: true,
        });
        props.onArchive(elementPropertyRule.id);
        props.onCloseSignal(false);
      },
      onUnarchive: () => {
        const customEventName = 'Property Extraction Rule Archived';
        FsEvent.recordCustomEventV2(customEventName, {
          id: elementPropertyRule.id,
          archived: false,
        });
        props.onUnarchive(elementPropertyRule.id);
        props.onCloseSignal(false);
      },
      archived,
      disableEdit: archived || !canModifyProperties,
      disableArchive:
        !canModifyProperties || !archived
          ? props.variant === ElementPropertyRuleModalVariant.new
          : !!errors.name,
      emptyMessage: intl.formatMessage(
        {
          defaultMessage:
            'Property Rule Preview<b></b>Load preview to start defining your property rule.',
          id: '1KAogp',
        },
        {
          b: () => <br />,
        },
      ),
      metadata,
    },
  };
};
